(function initHero() {
  'use strict';
  const surface = document.querySelector('.hero-reveal');
  if (!surface) return;
  const mobile = matchMedia('(max-width: 700px)');
  if (mobile.matches) {
    const onDesktop = event => { if (!event.matches) { mobile.removeEventListener('change', onDesktop); initHero(); } };
    mobile.addEventListener('change', onDesktop);
    return;
  }
  let canvas = surface.querySelector('canvas');
  const motion = matchMedia('(prefers-reduced-motion: reduce)');
  const mask = document.createElement('canvas');
  const ink = mask.getContext('2d');
  if (!ink) return;
  const TRAIL_MS = 1900;
  let width = 1, height = 1, scale = 1, frame = 0, previousTime = 0;
  let lastPaint = 0, inside = false, focused = false, visible = true;
  let pointer = {x: .5, y: .5}, previousPoint = null, pendingPoint = false;
  let renderer, photo, normal, sculpture;

  function loadImage(src) {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.crossOrigin = 'anonymous';
      image.onload = () => resolve(image);
      image.onerror = reject;
      image.src = src;
    });
  }

  async function loadTextures() {
    let sources = window.UrbanShop.config.heroTextures;
    if (location.protocol === 'file:') {
      // Local image origins are opaque in WebGL. A local script provides the same
      // PNG bytes as data URLs, retaining normal mapping when index.html is double-clicked.
      await new Promise(resolve => {
        const script = document.createElement('script');
        script.src = 'assets/hero/hat-bust-file.js?v=20260909-relief';
        script.onload = script.onerror = resolve;
        document.head.append(script);
      });
      if (window.URBAN_HERO_TEXTURES) sources = window.URBAN_HERO_TEXTURES;
      delete window.URBAN_HERO_TEXTURES;
    }
    return Promise.all([loadImage(sources[0]), loadImage(sources[1]).catch(() => null), loadImage(sources[2])]);
  }

  function makeWebGL() {
    const gl = canvas.getContext('webgl', {alpha: false, antialias: false, depth: false, stencil: false});
    if (!gl) throw new Error('WebGL unavailable');
    const shaders = [];
    let program, buffer;
    const textures = [];
    function destroy() {
      textures.forEach(texture => gl.deleteTexture(texture));
      shaders.forEach(shader => gl.deleteShader(shader));
      if (buffer) gl.deleteBuffer(buffer);
      if (program) gl.deleteProgram(program);
    }
    try {
      function compile(type, source) {
        const shader = gl.createShader(type);
        shaders.push(shader);
        gl.shaderSource(shader, source);
        gl.compileShader(shader);
        if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(shader));
        return shader;
      }
      program = gl.createProgram();
      gl.attachShader(program, compile(gl.VERTEX_SHADER, `
        attribute vec2 aPosition;
        varying vec2 vUV;
        void main() { vUV = aPosition * .5 + .5; gl_Position = vec4(aPosition, 0., 1.); }
      `));
      gl.attachShader(program, compile(gl.FRAGMENT_SHADER, `
        precision mediump float;
        varying vec2 vUV;
        uniform sampler2D uColor, uNormal, uMask, uSculpture;
        uniform vec2 uSize, uPointer, uImageScale;
        uniform float uFocus;
        float hash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
        float noise(vec2 p) {
          vec2 i = floor(p), f = fract(p); f = f * f * (3. - 2. * f);
          return mix(mix(hash(i), hash(i + vec2(1., 0.)), f.x),
                     mix(hash(i + vec2(0., 1.)), hash(i + vec2(1.)), f.x), f.y);
        }
        void main() {
          vec2 uv = (vUV - .5) / uImageScale + .5;
          if (uv.x < 0. || uv.x > 1. || uv.y < 0. || uv.y > 1.) {
            gl_FragColor = vec4(1.); return;
          }
          float mask = texture2D(uMask, vUV).a;
          vec3 base = mix(vec3(1.), texture2D(uSculpture, uv).rgb, .72);
          if (mask < .002) { gl_FragColor = vec4(base, 1.); return; }
          vec3 color = texture2D(uColor, uv).rgb;
          vec3 normal = normalize(texture2D(uNormal, uv).rgb * 2. - 1.);
          vec2 p = vUV * uSize;
          float grain = noise(p * .055) * .65 + noise(p * .19) * .35;
          // Noise and surface ridges break up the edge, without moving the photograph.
          float edge = grain * .22 + abs(normal.x + normal.y) * .025;
          float reveal = smoothstep(.07 + edge, .56 + edge, mask);
          reveal = mix(reveal, 1., uFocus);
          vec3 light = normalize(vec3((uPointer - vUV) * vec2(1., -1.), .8));
          float relief = (dot(normal, light) - normal.z) * .065;
          // Lighting is confined to the developing edge. Fully revealed pixels keep their original color.
          color *= 1. + relief * (1. - reveal) * (1. - uFocus);
          gl_FragColor = vec4(mix(base, color, reveal), 1.);
        }
      `));
      gl.linkProgram(program);
      if (!gl.getProgramParameter(program, gl.LINK_STATUS)) throw new Error(gl.getProgramInfoLog(program));
      gl.useProgram(program);
      buffer = gl.createBuffer();
      gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
      gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1,-1, 1,-1, -1,1, -1,1, 1,-1, 1,1]), gl.STATIC_DRAW);
      const position = gl.getAttribLocation(program, 'aPosition');
      gl.enableVertexAttribArray(position);
      gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);
      gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
      [photo, normal, mask, sculpture].forEach((image, unit) => {
        const texture = gl.createTexture();
        textures.push(texture);
        gl.activeTexture(gl.TEXTURE0 + unit);
        gl.bindTexture(gl.TEXTURE_2D, texture);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        if (image) gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, image);
        else gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 1, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, new Uint8Array([128,128,255,255]));
        gl.uniform1i(gl.getUniformLocation(program, ['uColor','uNormal','uMask','uSculpture'][unit]), unit);
      });
      const uniforms = Object.fromEntries(['uSize','uPointer','uImageScale','uFocus'].map(name => [name, gl.getUniformLocation(program, name)]));
      return {
        destroy,
        draw() {
          gl.viewport(0, 0, canvas.width, canvas.height);
          const fit = Math.min(width / photo.width, height / photo.height);
          gl.uniform2f(uniforms.uImageScale, photo.width * fit / width, photo.height * fit / height);
          gl.uniform2f(uniforms.uSize, width, height);
          gl.uniform2f(uniforms.uPointer, pointer.x, 1 - pointer.y);
          gl.uniform1f(uniforms.uFocus, focused ? 1 : 0);
          gl.activeTexture(gl.TEXTURE2);
          gl.bindTexture(gl.TEXTURE_2D, textures[2]);
          gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, mask);
          gl.drawArrays(gl.TRIANGLES, 0, 6);
        }
      };
    } catch (error) { destroy(); throw error; }
  }

  function makeFallback() {
    // Canvas compositing also works with file:// images, which WebGL texture security may reject.
    const replacement = document.createElement('canvas');
    replacement.setAttribute('aria-hidden', 'true');
    replacement.width = canvas.width;
    replacement.height = canvas.height;
    canvas.replaceWith(replacement);
    canvas = replacement;
    const ctx = canvas.getContext('2d');
    if (!ctx) return {draw() {}};
    return {draw() {
      const fit = Math.min(canvas.width / photo.width, canvas.height / photo.height);
      const w = photo.width * fit, h = photo.height * fit;
      ctx.globalCompositeOperation = 'source-over';
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(photo, (canvas.width - w) / 2, (canvas.height - h) / 2, w, h);
      ctx.globalCompositeOperation = 'destination-in';
      ctx.drawImage(mask, 0, 0, canvas.width, canvas.height);
      // The complete white sculpture stays visible underneath the colored trail.
      ctx.globalCompositeOperation = 'destination-over';
      ctx.globalAlpha = .72;
      ctx.drawImage(sculpture, (canvas.width - w) / 2, (canvas.height - h) / 2, w, h);
      ctx.globalAlpha = 1;
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.globalCompositeOperation = 'source-over';
    }};
  }

  function schedule() {
    if (!frame && renderer && visible && !document.hidden) frame = requestAnimationFrame(paint);
  }
  function clear() {
    ink.clearRect(0, 0, mask.width, mask.height);
    previousPoint = null;
    lastPaint = 0;
  }
  function resize() {
    const rect = surface.getBoundingClientRect();
    width = Math.max(1, rect.width); height = Math.max(1, rect.height);
    // Fit to the image, not the canvas: tall/narrow layouts have white letterboxing.
    const imageHeight = Math.min(height, width * 1540 / 1021);
    const imageTop = (height - imageHeight) / 2;
    for (const [name, fraction] of [['start', .76], ['soft', .84], ['tail', .94], ['end', 1]]) {
      surface.style.setProperty(`--relief-fade-${name}`, `${imageTop + imageHeight * fraction}px`);
    }
    scale = Math.min(devicePixelRatio || 1, 2);
    canvas.width = Math.round(width * scale); canvas.height = Math.round(height * scale);
    // The trail needs less resolution than the photograph; keep texture uploads small.
    mask.width = Math.max(1, Math.round(width * .65)); mask.height = Math.max(1, Math.round(height * .65));
    clear(); previousTime = 0; pendingPoint = inside;
    schedule();
  }
  function dab(x, y, radius) {
    const brush = ink.createRadialGradient(x, y, radius * .22, x, y, radius);
    brush.addColorStop(0, 'rgba(255,255,255,.98)');
    brush.addColorStop(.45, 'rgba(255,255,255,.78)');
    brush.addColorStop(1, 'rgba(255,255,255,0)');
    ink.fillStyle = brush;
    ink.fillRect(x - radius, y - radius, radius * 2, radius * 2);
  }
  function paint(now) {
    frame = 0;
    const dt = Math.min(64, previousTime ? now - previousTime : 16);
    previousTime = now;
    if (focused) {
      ink.fillStyle = '#fff'; ink.fillRect(0, 0, mask.width, mask.height);
      renderer.draw(); return;
    }
    if (motion.matches) clear();
    else {
      ink.globalCompositeOperation = 'destination-out';
      ink.fillStyle = `rgba(0,0,0,${1 - Math.exp(-dt / 480)})`;
      ink.fillRect(0, 0, mask.width, mask.height);
      ink.globalCompositeOperation = 'source-over';
    }
    if (inside || pendingPoint) {
      const x = pointer.x * mask.width, y = pointer.y * mask.height;
      const radius = Math.min(145, Math.max(62, width * .31)) * mask.width / width;
      const start = previousPoint || {x, y};
      const steps = Math.min(48, Math.max(1, Math.ceil(Math.hypot(x - start.x, y - start.y) / (radius * .18))));
      for (let i = 1; i <= steps; i++) dab(start.x + (x - start.x) * i / steps, start.y + (y - start.y) * i / steps, radius);
      previousPoint = {x, y}; lastPaint = now; pendingPoint = false;
    }
    if (!inside && now - lastPaint >= TRAIL_MS) clear();
    renderer.draw();
    if (!motion.matches && (inside || lastPaint > 0)) schedule();
  }
  function move(event) {
    if (event.pointerType === 'touch' && event.type === 'pointerenter') return;
    const rect = surface.getBoundingClientRect();
    pointer = {x: Math.max(0, Math.min(1, (event.clientX - rect.left) / width)), y: Math.max(0, Math.min(1, (event.clientY - rect.top) / height))};
    inside = true; pendingPoint = true; schedule();
  }
  function leave() { inside = false; pendingPoint = false; previousPoint = null; schedule(); }
  surface.addEventListener('pointerenter', move);
  surface.addEventListener('pointermove', move);
  surface.addEventListener('pointerdown', move);
  surface.addEventListener('pointerleave', leave);
  surface.addEventListener('pointercancel', leave);
  surface.addEventListener('pointerup', event => {
    if (event.pointerType !== 'mouse') { inside = false; previousPoint = null; schedule(); }
  });
  surface.addEventListener('focus', () => { focused = surface.matches(':focus-visible'); clear(); schedule(); });
  surface.addEventListener('blur', () => { focused = false; clear(); schedule(); });
  function pause() {
    cancelAnimationFrame(frame); frame = 0; previousTime = 0;
    inside = false; pendingPoint = false; clear();
    if (renderer) renderer.draw();
  }
  document.addEventListener('visibilitychange', () => { if (document.hidden) pause(); else schedule(); });
  window.addEventListener('blur', pause);
  window.addEventListener('pagehide', pause);
  window.addEventListener('pageshow', schedule);
  motion.addEventListener('change', () => { clear(); schedule(); });
  if ('IntersectionObserver' in window) new IntersectionObserver(entries => {
    visible = entries[0].isIntersecting;
    if (visible) schedule(); else pause();
  }).observe(surface);
  if ('ResizeObserver' in window) new ResizeObserver(resize).observe(surface);
  else window.addEventListener('resize', resize, {passive: true});

  loadTextures().then(images => {
    [photo, normal, sculpture] = images;
    resize();
    try { renderer = makeWebGL(); surface.dataset.renderer = 'webgl'; }
    catch { renderer = makeFallback(); surface.dataset.renderer = 'canvas'; }
    canvas.addEventListener('webglcontextlost', event => {
      event.preventDefault(); pause(); renderer = makeFallback(); surface.dataset.renderer = 'canvas'; schedule();
    }, {once: true});
    schedule();
  }).catch(() => {
    surface.removeAttribute('tabindex');
    surface.setAttribute('aria-label', 'A white sculpture of a marble bust wearing four flower-trimmed straw hats.');
  });
})();
